import { AxiosError, AxiosResponse, AxiosStatic } from "axios";
import * as moment from "moment";

import { AnsweredQuestionWithId } from "@teamprevent/common/questionnaires/model";
import { TaskModel } from "@teamprevent/common/task/taskActions/models";
import { ConsultationDetails, parseConsultationModel } from "@teamprevent/common/consultations/models";
import { ServerModelWithDateStrings } from "@teamprevent/common/utils";

import { AddTaskFormModel, parseTaskModel, AddTaskModel, CreateTaskDto, mapAddTaskFormModelToCreateTaskDto } from "../task/model";
import { FollowUpTaskModel } from "../immunisationReview/model";
import { mapCreateTaskDtoToServer } from "../crud/ohCase/ohCaseApiMappers";

import { ClinicalNotesAutoSaveDto, DidNotAttendResponse } from "./models";

export class ClinicalNotesApi {
    private readonly apiUrl = "/api/clinicalNotes";

    private axios: AxiosStatic;

    constructor(ax: AxiosStatic) {
        this.axios = ax;
    }

    /**
     * @deprecated abortController is optional to keep code backwards compatible, we should make it mandatory as soon as practical
     */
    public async load(ohCaseId: string, taskId: string): Promise<ClinicalNotesAutoSaveDto>;
    public async load(ohCaseId: string, taskId: string, abortController: AbortController): Promise<ClinicalNotesAutoSaveDto>; // eslint-disable-line @typescript-eslint/unified-signatures, max-len

    public async load(ohCaseId: string, taskId: string, abortController?: AbortController): Promise<ClinicalNotesAutoSaveDto> {
        const response = await this.axios.get<
            ServerModelWithDateStrings<ClinicalNotesAutoSaveDto>
        >(`${this.apiUrl}/${ohCaseId}/${taskId}`, { signal: abortController?.signal });
        return response.data;
    }

    public async loadConsultation(ohCaseId: string, taskId: string): Promise<ConsultationDetails> {
        const response = await this.axios.get<
            ServerModelWithDateStrings<ConsultationDetails>
        >(`${this.apiUrl}/${ohCaseId}/${taskId}/consultation`);
        return parseConsultationModel(response.data);
    }

    public async submitReferralClinicalNotes(ohCaseId: string, taskId: string): Promise<TaskModel[]> {
        const response = await this.axios.post<ServerModelWithDateStrings<TaskModel>[]>(`${this.apiUrl}/referral/${ohCaseId}/${taskId}/complete`);
        return (response.data).map(parseTaskModel);
    }

    public async saveReferralClinicalNotes(
        ohCaseId: string, taskId: string, questions: AnsweredQuestionWithId[], eTag: string
    ): Promise<ClinicalNotesAutoSaveDto> {
        try {
            return (await this.axios.post(`${this.apiUrl}/referral/${ohCaseId}/${taskId}`, { data: { questions }, eTag }))?.data;
        } catch (ex) {
            const axiosError = ex as AxiosError;
            if (axiosError.response?.status === 400) {
                const error = { message: "Could not save as a newer version exists", status: 400 };
                throw error;
            } else {
                throw ex;
            }
        }
    }

    public async submitPhysiotherapyReferralClinicalNotes(ohCaseId: string, taskId: string): Promise<TaskModel[]> {
        const response = await this.axios.post<ServerModelWithDateStrings<TaskModel>[]>(`${this.apiUrl}/physiotherapyReferral/${ohCaseId}/${taskId}/complete`);
        return (response.data).map(parseTaskModel);
    }

    public async savePhysiotherapyReferralClinicalNotes(
        ohCaseId: string, taskId: string, questions: AnsweredQuestionWithId[], eTag: string
    ): Promise<ClinicalNotesAutoSaveDto> {
        try {
            return (await this.axios.post(`${this.apiUrl}/physiotherapyReferral/${ohCaseId}/${taskId}`, { data: { questions }, eTag }))?.data;
        } catch (ex) {
            const axiosError = ex as AxiosError;
            if (axiosError.response?.status === 400) {
                const error = { message: "Could not save as a newer version exists", status: 400 };
                throw error;
            } else {
                throw ex;
            }
        }
    }

    public submitPpqClinicalNotes(ohCaseId: string, taskId: string, questions: AnsweredQuestionWithId[], tasks: AddTaskFormModel[]): Promise<TaskModel[]>
    {
        return this.submit(`${this.apiUrl}/ppq/${ohCaseId}/${taskId}/complete`, ohCaseId, questions, tasks);
    }

    public submitPpqClinicalNotesAmendments(
        ohCaseId: string, taskId: string, questions: AnsweredQuestionWithId[], tasks: AddTaskFormModel[], reasonForEdit: string): Promise<TaskModel[]>
    {
        return this.submit(`${this.apiUrl}/ppq/${ohCaseId}/${taskId}/completeAmendments`, ohCaseId, questions, tasks, reasonForEdit);
    }

    private async submit(
        url: string, ohCaseId: string, questions: AnsweredQuestionWithId[], tasks: AddTaskFormModel[], reasonForEdit?: string): Promise<TaskModel[]>
    {
        try {
            const taskModels = tasks.map(t => mapCreateTaskDtoToServer(mapAddTaskFormModelToCreateTaskDto(t, ohCaseId)));
            const response = await this.axios.post<
                ServerModelWithDateStrings<TaskModel>[],
                AxiosResponse<ServerModelWithDateStrings<TaskModel>[]>,
                ServerModelWithDateStrings<{ notes: { questions: AnsweredQuestionWithId[] }; tasks: CreateTaskDto[]; reasonForEdit: string }>
            >(
                url,
                { notes: { questions }, tasks: taskModels, reasonForEdit: reasonForEdit ?? "" }
            );
            return (response.data).map(parseTaskModel);
        } catch (ex) {
            const axiosError = ex as AxiosError;
            if (axiosError?.response?.status === 400) {
                const error = { message: axiosError?.response?.data, status: 400 };
                throw error;
            } else {
                throw ex;
            }
        }
    }

    public async savePpqClinicalNotes(ohCaseId: string, taskId: string, questions: AnsweredQuestionWithId[], eTag: string): Promise<ClinicalNotesAutoSaveDto> {
        try {
            return (await this.axios.post(`${this.apiUrl}/ppq/${ohCaseId}/${taskId}`, { data: { questions }, eTag }))?.data;
        } catch (ex) {
            const axiosError = ex as AxiosError;
            if (axiosError.response?.status === 400) {
                const error = { message: "Could not save as a newer version exists", status: 400 };
                throw error;
            } else {
                throw ex;
            }
        }
    }

    public async submitImmunisationBloodTestConsultation(
        ohCaseId: string, taskId: string, questions: AnsweredQuestionWithId[], tasks: FollowUpTaskModel[], immunisationTypeTaskIds: string[]
    ): Promise<TaskModel[]> {
        const parsedTasksModel = tasks.map<ServerModelWithDateStrings<FollowUpTaskModel>>(m => {
            return {
                ...m,
                dueDate: m.dueDate?.format(moment.HTML5_FMT.DATETIME_LOCAL_SECONDS)
            };
        });

        const response = await this.axios.post<ServerModelWithDateStrings<TaskModel>[]>(
            `${this.apiUrl}/immunisation-blood-test-consultation/${ohCaseId}/${taskId}`,
            { questions: { questions }, tasks: parsedTasksModel, immunisationTypeTaskIds });
        return (response.data).map(parseTaskModel);
    }

    public async submitBbvClinicalNotes(
        ohCaseId: string, taskId: string, questions: AnsweredQuestionWithId[], task: AddTaskFormModel): Promise<TaskModel[]> {
        const taskModel = {
            taskType: task.taskType?.type,
            taskCodeId: task.taskCode,
            dueDate: task.dueDate,
            notes: task.notes,
            assignedUsers: task.assignedUsers
        };
        const response = await this.axios.post<ServerModelWithDateStrings<TaskModel>[]>(
            `${this.apiUrl}/bbv/${ohCaseId}/${taskId}`,
            { questions: { questions }, task: taskModel });
        return (response.data).map(parseTaskModel);
    }

    public async submitVaccinationConsultation(
        ohCaseId: string,
        taskId: string,
        questions: AnsweredQuestionWithId[],
        tasks: FollowUpTaskModel[],
        immunisationTypeTaskIds: string[]): Promise<TaskModel[]> {

        const parsedTasksModel = tasks.map<ServerModelWithDateStrings<FollowUpTaskModel>>(m => {
            return {
                ...m,
                dueDate: m.dueDate?.format(moment.HTML5_FMT.DATETIME_LOCAL_SECONDS)
            };
        });

        const data = { form: { questions }, tasks: parsedTasksModel, immunisationTypeTaskIds };
        const response = await this.axios.post<ServerModelWithDateStrings<TaskModel>[]>(`${this.apiUrl}/vaccinationconsultation/${ohCaseId}/${taskId}`, data);
        return (response.data).map(parseTaskModel);
    }

    public async submitBbvConsultation(
        ohCaseId: string,
        taskId: string,
        questions: AnsweredQuestionWithId[],
        tasks: AddTaskModel[]): Promise<TaskModel[]> {
        try {
            const taskModels = tasks.map(task => ({
                taskType: task.type,
                taskCodeId: task.taskCodeId,
                dueDate: task.dueDate,
                notes: task.notes,
                assignedUsers: task.assignedUsers
            }));
            const response = await this.axios.post<ServerModelWithDateStrings<TaskModel>[]>(
                `${this.apiUrl}/bbvconsultation/${ohCaseId}/${taskId}/complete`,
                { form: { questions }, tasks: taskModels }
            );
            return (response.data).map(parseTaskModel);
        } catch (ex) {
            const axiosError = ex as AxiosError;
            const error = { message: axiosError?.response?.data, status: axiosError?.response?.status };
            throw error;
        }
    }

    public async saveBbvConsultation(ohCaseId: string, taskId: string, questions: AnsweredQuestionWithId[], eTag: string): Promise<ClinicalNotesAutoSaveDto> {
        try {
            return (await this.axios.post<ClinicalNotesAutoSaveDto>(
                `${this.apiUrl}/bbvconsultation/${ohCaseId}/${taskId}`,
                { data: { questions }, eTag }))?.data;
        } catch (ex) {
            const axiosError = ex as AxiosError;
            if (axiosError.response?.status === 400) {
                const error = { message: "Could not save as a newer version exists", status: 400 };
                throw error;
            } else {
                throw ex;
            }
        }
    }

    public async didNotAttend(ohCaseId: string, taskId: string, comment: string): Promise<TaskModel[]> {
        const response = await this.axios.post<ServerModelWithDateStrings<TaskModel>[]>(`${this.apiUrl}/${ohCaseId}/${taskId}/didNotAttend`,
            { comment });

        return (response.data)
            .map(parseTaskModel);
    }

    public async didNotAttendMultiple(appointmentTasks: TaskModel[], comment: string): Promise<DidNotAttendResponse[]> {
        const response = await this.axios.post<DidNotAttendResponse[]>(`${this.apiUrl}/didNotAttendMultiple`, {
            appointmentTasks,
            comment
        });

        return response.data.map(r => {
            return {
                ohCaseId: r.ohCaseId,
                appointmentTasks: r.appointmentTasks.map(parseTaskModel)
            };
        });
    }
}
